/**
 * Cart-drawer apps that render outside the theme's DOM or navigate from script (D212).
 *
 * UpCart (AfterSell / Rokt), read off its theme-app-extension bundle
 * `cdn.shopify.com/extensions/01a0d532-…/upcart-2-413/assets/upcart-bundle.js` and the live
 * settings on lysesbjerg.com, 2026-09-29:
 *
 * - The drawer mounts into `<div id="upCart">`. With the setting `renderCartInShadowDom` (default
 *   `true` on new installs, `valueOnFirstInstallOverride`; `true` on lysesbjerg.com) it calls
 *   `container.attachShadow({mode:"open"})` and renders the whole cart **inside that shadow
 *   root**. A click on its checkout button then reaches `document` retargeted to the host
 *   `#upCart`, so `event.target.closest(…)` never sees the button — the miss this fixes.
 *   `composedPath()[0]` is the real node for an open root.
 * - The checkout button is `<div class="upcart-checkout-button-container styles_CustomButton__">
 *   <a href="{url}" class="… UpcartDesignSettings__button upcart-checkout-button"
 *   data-disable-recharge="true">`. `{url}` is `Cart.checkoutUrl` from the Storefront API
 *   (`/cart/c/{token}?key=…`), `{root}checkout` until that answers, `/cart` when the merchant set
 *   `redirectToCart`, a Recharge URL for subscription carts, and absent while UpCart disables the
 *   button. Its React `onClick` calls `preventDefault()` and sets
 *   `document.location.href = url` synchronously — so the navigation is scripted, not the
 *   anchor's default action.
 * - Before navigating it dispatches `aftersell-upcart:public-events:checkout-clicked` on `window`
 *   (`cancelable: false`) and calls `window.upcartOnCheckout` / `window.upcartCheckoutListeners`,
 *   each wrapped so a throw is swallowed. None of these can stop or redirect the navigation, so
 *   there is no documented hook to use: the capture-phase click intercept is the way in.
 * - Discount codes, the note and cart attributes are written to the cart when the shopper edits
 *   them (`/discount/{code}`, Storefront API `cartDiscountCodesUpdate`, `/cart/update.js`), not at
 *   click time, so the `cart.js` the handoff reads already carries them.
 * - Express buttons render inside the same shadow root (`#upcart-express-payments`,
 *   `#upcart-express-pay-buttons-container`, `.upcart-express-pay-button`, a clone of
 *   `#dynamic-checkout-cart`), where a stylesheet in the document `<head>` does not apply.
 */

import type { YcDocumentLike } from './express';

/** UpCart's drawer host element; its open shadow root holds the cart when shadow rendering is on. */
export const UPCART_ROOT_ID = 'upCart';

/** UpCart's window events after which the drawer's DOM exists (`aftersell-upcart:public-events:*`). */
export const UPCART_RENDER_EVENTS: readonly string[] = ['cart-loaded', 'cart-opened'].map(
  (name) => 'aftersell-upcart:public-events:' + name,
);

/**
 * The node a pointer event really started on.
 *
 * `composedPath()[0]` crosses open shadow roots (UpCart's); `target` is the fallback for a
 * browser without `composedPath` or a closed root, where it is the best there is.
 */
export function originalTarget(event: Event): Element | null {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const first = path.length > 0 ? path[0] : event.target;
  return first instanceof Element ? first : null;
}

/** `closest()` that continues from a shadow root to its host, up to the document. */
export function closestComposed(element: Element, selector: string): Element | null {
  let current: Element | null = element;
  while (current !== null) {
    const found = current.closest(selector);
    if (found !== null) return found;
    // A shadow root has a `host`; the document does not. Duck-typed so a browser without a
    // `ShadowRoot` global cannot throw on every click.
    const host: unknown = (current.getRootNode() as { host?: unknown }).host;
    current = host instanceof Element ? host : null;
  }
  return null;
}

/**
 * A `YcDocumentLike` over UpCart's shadow root, so the express-button stylesheet can be put
 * where UpCart's express buttons are; `null` when UpCart is absent or renders in the light DOM.
 */
export function upcartShadowDocument(doc: Document): YcDocumentLike | null {
  const host = doc.getElementById(UPCART_ROOT_ID);
  const root = host === null ? null : host.shadowRoot;
  if (root === null) return null;
  return {
    querySelector: (selector) => root.querySelector(selector),
    createElement: (tagName) => doc.createElement(tagName),
    head: root,
    documentElement: root,
  };
}
